/**
 * CanvasPalette — the curated left rail (spec §7). Four groups from the registries:
 * Evidence In (opens Build / Import), Operators (run on selected node or right-click),
 * Algorithms (honest status chip), Playbooks. Status dot = live / wip / build / roadmap.
 */
import { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, ChevronRight, Play, LayoutGrid } from 'lucide-react';
import {
  EVIDENCE, OPERATORS, ALGORITHMS, PLAYBOOKS, STATUS_META, type CanvasStatus, type EvidenceType,
} from '../../case-canvas/registry';
import { runEvidence, runOperatorFromPalette, runAlgorithmFromPalette } from '../../case-canvas/jobs';
import { runPlaybook } from '../../case-canvas/playbookRunner';
import { useCaseWriteBlock } from '../../case-canvas/caseAccess';
import { EdgeCollapseTab } from '../workbench/EdgeCollapseTab';

const AUTOPILOT_ID = 'ncrp_to_national_linkage';
const PALETTE_KEY = 'fusion-palette-collapsed';

function readCollapsed(): boolean {
  try {
    const v = localStorage.getItem(PALETTE_KEY);
    // Default collapsed so the canvas dominates on first paint; officers expand
    // via the edge tab when adding evidence / running Autopilot.
    if (v === null) return true;
    return v === '1';
  } catch {
    return true;
  }
}

function Dot({ status }: { status: CanvasStatus }) {
  const m = STATUS_META[status];
  return <span title={m.label} className="w-1.5 h-1.5 rounded-full inline-block shrink-0" style={{ background: m.color }} />;
}

function Group({ title, count, defaultOpen, children }: { title: string; count: number; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border-b border-border">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}{title}<span className="ml-auto text-[10px] opacity-60">{count}</span>
      </button>
      {open && <div className="pb-1">{children}</div>}
    </div>
  );
}

function evidenceHint(ev: EvidenceType): string {
  if (ev.id === 'E1') return 'Opens Bank import';
  if ((ev.status === 'new' || ev.status === 'roadmap') && !ev.produces) return `${STATUS_META[ev.status].label} — not runnable yet`;
  return 'Opens Build card';
}

export function CanvasPalette() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // A viewer collaborator reads the case and never acts on it: every control that writes is disabled with the reason.
  const { blocked, reason: blockedReason } = useCaseWriteBlock();

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(PALETTE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div
      className={clsx(
        'relative z-20 shrink-0 flex flex-col min-h-0 self-stretch transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-11' : 'w-60',
      )}
      data-testid="fusion-palette"
      data-collapsed={collapsed ? 'true' : 'false'}
      data-tour="fusion-palette"
    >
      <aside className="flex flex-col flex-1 min-h-0 overflow-hidden border-r border-border bg-card text-xs">
        {collapsed ? (
          <div className="flex flex-col items-center py-2 gap-2" aria-hidden={false}>
            <button
              type="button"
              onClick={toggle}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Expand palette"
              aria-label="Expand palette"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <div className="mt-1 text-[10px] font-medium text-muted-foreground tracking-wide select-none [writing-mode:vertical-rl] rotate-180">
              Palette
            </div>
          </div>
        ) : (
          <>
            <div className="px-3 py-2.5 border-b border-border font-semibold text-sm shrink-0 bg-card/50">Palette</div>
            <div className="px-3 py-1.5 border-b border-border flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[9px] text-muted-foreground shrink-0 bg-secondary/20">
              {(['live', 'wip', 'new', 'roadmap'] as CanvasStatus[]).map((s) => (
                <span key={s} className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: STATUS_META[s].color }} />
                  {STATUS_META[s].label}
                </span>
              ))}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              <Group title="Evidence In" count={EVIDENCE.length} defaultOpen>
                {EVIDENCE.map((ev) => {
                  const disabled = (ev.status === 'new' || ev.status === 'roadmap') && !ev.produces;
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => runEvidence(ev)}
                      disabled={disabled || blocked}
                      title={blocked ? blockedReason : disabled ? `${STATUS_META[ev.status].label} · ${ev.binding}` : `${ev.desc} (${evidenceHint(ev)})`}
                      data-testid={`palette-evidence-${ev.id}`}
                      className="w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-secondary/80 transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
                    >
                      <ev.icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1.5"><span className="font-medium truncate">{ev.name}</span><Dot status={ev.status} /></span>
                        <span className="block text-[10px] text-muted-foreground truncate">{evidenceHint(ev)}</span>
                      </span>
                    </button>
                  );
                })}
              </Group>

              <Group title="Operators" count={OPERATORS.length}>
                {OPERATORS.map((op) => {
                  // LIVE = starts a canvas job that writes to the case graph. Anything
                  // else is visibly unavailable, with the reason on hover.
                  const runnable = STATUS_META[op.status].live;
                  return (
                    <button
                      key={op.id}
                      type="button"
                      onClick={() => runOperatorFromPalette(op)}
                      disabled={!runnable || blocked}
                      title={blocked ? blockedReason : runnable
                        ? `${op.desc} · ${op.scope === 'case' ? 'runs on the whole case' : `on ${op.availableOn.join(', ')}. Or right-click a matching node.`}`
                        : `${STATUS_META[op.status].label} · unavailable — ${op.unavailable || op.binding}`}
                      data-testid={`palette-operator-${op.id}`}
                      className="w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-secondary/80 transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
                    >
                      <op.icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1.5"><span className="font-medium truncate">{op.name}</span><Dot status={op.status} /></span>
                        <span className="block text-[10px] text-muted-foreground truncate">
                          {!runnable
                            ? `Unavailable · ${op.unavailable || STATUS_META[op.status].label}`
                            : op.scope === 'case'
                              ? 'Runs on the whole case'
                              : `Select a ${op.availableOn[0]} · or right-click`}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </Group>

              <Group title="Algorithms" count={ALGORITHMS.length}>
                {ALGORITHMS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => runAlgorithmFromPalette(a.id)}
                    title={a.binding}
                    data-testid={`palette-algorithm-${a.id}`}
                    className="w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-secondary/80 transition-colors"
                  >
                    <a.icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5"><span className="font-medium truncate">{a.name}</span><Dot status={a.status} /></span>
                      <span className="block text-[10px] text-muted-foreground truncate">{STATUS_META[a.status].label} · not runnable yet</span>
                    </span>
                  </button>
                ))}
              </Group>

              <Group title="Playbooks" count={PLAYBOOKS.length}>
                {PLAYBOOKS.map((p) => {
                  const runnable = p.id === AUTOPILOT_ID;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={runnable ? () => { void runPlaybook(); } : undefined}
                      disabled={!runnable || blocked}
                      title={blocked ? blockedReason : runnable ? 'Run Autopilot over the synthetic case' : `${p.desc} — not wired yet`}
                      data-testid={`palette-playbook-${p.id}`}
                      className="w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-secondary/80 transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
                    >
                      {runnable ? <Play className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" /> : <span className="w-3.5 shrink-0" />}
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1.5"><span className="font-medium truncate">{p.name}</span><Dot status={p.status} /></span>
                        <span className="block text-[10px] text-muted-foreground truncate">
                          {runnable ? 'Autopilot · five-act demo arc' : 'Not wired yet'}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </Group>
            </div>

            <div className="mt-auto px-3 py-2 text-[10px] text-muted-foreground border-t border-border leading-relaxed shrink-0">
              Evidence opens Build. Operators need a selected node (or right-click).
            </div>
          </>
        )}
      </aside>
      <EdgeCollapseTab
        side="right"
        direction={collapsed ? 'expand' : 'collapse'}
        onClick={toggle}
        label={collapsed ? 'Expand palette' : 'Collapse palette'}
      />
    </div>
  );
}
